import React, { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { jwtVerify } from 'jose';
import clientLogger from './utils/clientLogger';
import { prysmDB } from './utils/database';
import apiService from './services/apiService';
import awsRumService from './services/awsRum';
import Welcome from './pages/Welcome';
import Registration from './pages/Registration';
import SelfieUpload from './pages/SelfieUpload';
import LicenseUpload from './pages/LicenseUpload';
import Checkout from './pages/Checkout';
import Profile from './pages/Profile';
import SMSVerification from './pages/SMSVerification';
import Navbar from './components/Navbar';
import NotFound from './pages/NotFound';
import ErrorBoundary from './components/ErrorBoundary';
import ThankYou from './pages/ThankYou';
import Payment from './pages/Payment';
import SupportQA from './pages/SupportQA';
import SquarePaymentCard from './components/SquarePaymentCard';
import './styles/global.css';

// Default values if environment variables are not set
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
const COOKIE_NAME = process.env.REACT_APP_COOKIE_NAME || 'prysm-registration';

console.log('[App] Environment variables:', {
  REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
  REACT_APP_COOKIE_NAME: process.env.REACT_APP_COOKIE_NAME,
  BACKEND_URL: BACKEND_URL,
  COOKIE_NAME: COOKIE_NAME
});

// Registration Guard Component
function RegistrationGuard({ children }) {
  const [isRegistrationComplete, setIsRegistrationComplete] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        // Check for registration data in multiple possible locations
        const registrationData = await prysmDB.get('registration_data', 'current_registration');
        const registrationResponse = await prysmDB.get('registration_data', 'registration_response');

        console.log('[RegistrationGuard] Registration data check:', {
          hasCurrentRegistration: !!registrationData,
          hasRegistrationResponse: !!registrationResponse,
          currentRegistrationKeys: registrationData ? Object.keys(registrationData) : null
        });

        // Registration is complete if we have either the current registration data or the response
        const isComplete = !!(registrationData || registrationResponse);
        setIsRegistrationComplete(isComplete);
      } catch (error) {
        console.error('[RegistrationGuard] Error checking registration status:', error);
        setIsRegistrationComplete(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkRegistrationStatus();
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-pink-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isRegistrationComplete) {
    console.log('[RegistrationGuard] Registration not complete, redirecting to /register');
    return <Navigate to="/register" replace />;
  }

  return children;
}

function AppContent() {
  console.log('[App] AppContent component rendering...');
  console.log('[App] Current URL:', window.location.href);
  console.log('[App] Current pathname:', window.location.pathname);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize AWS RUM on app start
  useEffect(() => {
    const initializeRum = async () => {
      try {
        console.log('[App] Initializing AWS RUM...');
        const rumInitialized = await awsRumService.initialize();
        
        if (rumInitialized) {
          console.log('[App] AWS RUM initialized successfully');
          
          // Set initial session attributes
          awsRumService.addSessionAttributes({
            applicationName: 'prysm-registration',
            environment: process.env.NODE_ENV || 'development',
            version: process.env.REACT_APP_VERSION || '1.0.0',
            userAgent: navigator.userAgent,
            initialUrl: window.location.href,
            sessionStartTime: new Date().toISOString()
          });
          
          // Track application start
          awsRumService.recordEvent('application_start', {
            url: window.location.href,
            pathname: window.location.pathname,
            search: window.location.search,
            referrer: document.referrer,
            timestamp: new Date().toISOString()
          });
          
          // Set up global error tracking
          window.addEventListener('error', (event) => {
            awsRumService.trackError(event.error, {
              type: 'global_error',
              filename: event.filename,
              lineno: event.lineno,
              colno: event.colno
            });
          });
          
          // Set up unhandled promise rejection tracking
          window.addEventListener('unhandledrejection', (event) => {
            awsRumService.trackError(new Error(event.reason), {
              type: 'unhandled_promise_rejection',
              reason: event.reason
            });
          });
          
          // Store session start time for drop-off calculations
          window.sessionStartTime = Date.now();
          
        } else {
          console.log('[App] AWS RUM initialization skipped (not configured)');
        }
      } catch (error) {
        console.error('[App] Failed to initialize AWS RUM:', error);
      }
    };
    
    initializeRum();
  }, []);


  // Decode JWT token from server using jose library
  const decodeJwtFromServer = async (token) => {
    try {
      console.log('[App] Attempting to decode JWT token...');

      // Check if Web Crypto API is available
      if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
        console.log('[App] Using Web Crypto API for JWT verification');
        // Create secret key from JWT secret (same as server)
        const secret = new TextEncoder().encode(process.env.REACT_APP_JWT_SECRET || 'development-secret-key-change-in-production');

        // Verify JWT token using jose library
        const { payload } = await jwtVerify(token, secret, {
          algorithms: ['HS256']
        });

        return payload;
      } else {
        console.log('[App] Web Crypto API not available, using fallback JWT decoding');
        // Fallback: Simple JWT decode without verification (for development)
        const parts = token.split('.');
        if (parts.length !== 3) {
          throw new Error('Invalid JWT format');
        }

        const payload = JSON.parse(atob(parts[1]));
        console.log('[App] JWT payload decoded (unverified):', payload);

        // Check if token is expired
        if (payload.exp && payload.exp < Date.now() / 1000) {
          throw new Error('Token expired');
        }

        return payload;
      }
    } catch (error) {
      console.error('[App] JWT decoding error:', error);
      throw new Error('Invalid token');
    }
  };

  // Request new session token from server
  const requestNewSession = async () => {
    try {
      console.log('[App] Requesting new session token from server...');
      console.log('[App] Backend URL for createToken:', BACKEND_URL);

      // Add timeout to createToken API call
      const tokenPromise = apiService.createToken();
      const tokenTimeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Create token timeout')), 15000)
      );

      const response = await Promise.race([tokenPromise, tokenTimeoutPromise]);
      console.log('[App] createToken response received:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        console.error('[App] createToken failed with status:', response.status);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log('[App] Parsing response JSON...');
      const responseData = await response.json();
      console.log('[App] createToken response data:', {
        success: responseData.success,
        hasToken: !!responseData.token,
        error: responseData.error
      });

      if (responseData.success) {
        // Get the token from the response or cookie
        const token = responseData.token || Cookies.get(COOKIE_NAME);
        console.log('[App] Token source:', {
          fromResponse: !!responseData.token,
          fromCookie: !!Cookies.get(COOKIE_NAME),
          tokenLength: token ? token.length : 0
        });

        if (!token) {
          console.error('[App] No token received from server or cookies');
          throw new Error('No token received from server');
        }

        // Decode the server-generated token
        console.log('[App] Decoding new token...');
        const decoded = await decodeJwtFromServer(token);
        console.log('[App] New token decoded successfully:', { uuid: decoded.uuid, exp: decoded.exp });
        setUser(decoded);

        // Store the token in a way that can be accessed by other components
        // Since the cookie might be HttpOnly, we'll store it in localStorage as backup
        console.log('[App] Storing token in localStorage...');
        localStorage.setItem('auth_token', token);

        // Set UUID in storage with timeout
        console.log('[App] Setting new UUID in storage...');
        try {
          const newUuidPromise = prysmDB.set('visitorUUID', 'current_uuid', { uuid: decoded.uuid });
          const newUuidTimeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('New UUID setting timeout')), 5000)
          );

          await Promise.race([newUuidPromise, newUuidTimeoutPromise]);
        } catch (newUuidError) {
          console.error('[App] Error setting new UUID in storage:', newUuidError);
          // Continue anyway
        }

        // Dispatch custom event to notify components of UUID update
        window.dispatchEvent(new Event('uuidUpdated'));
        console.log('[App] New UUID event dispatched');

        setError(null);
        console.log('[App] New session created successfully');
      } else {
        console.error('[App] createToken failed:', responseData.error);
        throw new Error(responseData.error?.message || 'Failed to create new session');
      }
    } catch (error) {
      console.error('[App] Session creation error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        type: typeof error
      });
      setError({
        message: 'Failed to create a new session. Please try refreshing the page.',
        type: 'creation'
      });

      // Log API error to server
      console.log('[App] Logging API error to clientLogger...');
      try {
        clientLogger.logApiError('/api/auth/create-token', error, {
          action: 'create_new_session',
          method: 'POST'
        });
      } catch (logError) {
        console.error('[App] Failed to log API error:', logError);
      }
    } finally {
      // Always clear loading state after session creation attempt
      console.log('[App] Clearing loading state after session creation');
      setIsLoading(false);
    }
  };

  // Reset session function - can be called from other components
  const resetSession = async () => {
    console.log('[App] Resetting session...');

    // Clear existing cookie
    Cookies.remove(COOKIE_NAME);

    // Clear localStorage token
    localStorage.removeItem('auth_token');

    // Completely delete the storage database
    try {
      await prysmDB.deleteDatabase();
      console.log('[App] storage database completely deleted');
    } catch (error) {
      console.error('[App] Error deleting storage database:', error);
      // Log the error but don't fail the reset process
      clientLogger.logError('session_reset_storage', `Failed to delete storage database during session reset: ${error.message}`, error, {
        context: 'reset_session_delete_storage'
      });
    }

    // Request new session
    await requestNewSession();
  };

  useEffect(() => {
    const checkAuth = async () => {
      console.log('[App] Starting authentication check...');
      setIsLoading(true);
      setError(null); // Clear any previous errors

      // Global timeout for the entire auth check to prevent infinite hanging
      const globalTimeout = setTimeout(() => {
        console.error('[App] Authentication check timed out after 30 seconds');
        setError({
          message: 'Authentication check timed out. Please refresh the page.',
          type: 'timeout'
        });
        setIsLoading(false);
      }, 30000);

      // Check for newuser=y URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const isNewUser = urlParams.get('newuser') === 'y';

      if (isNewUser) {
        console.log('[App] newuser=y detected, resetting session and getting new token...');

        // Clean URL parameters to prevent repeated resets
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        console.log('[App] URL cleaned, removed newuser parameter');

        // Reset session and get new token
        clearTimeout(globalTimeout);
        await resetSession();
        // Loading state will be cleared by requestNewSession's finally block
        return;
      }

      // Try to get token from localStorage first, then cookie
      const token = localStorage.getItem('auth_token') || Cookies.get(COOKIE_NAME);
      
      // Check for existing UUID
      let existingUUID = null;
      try {
        const uuidData = await prysmDB.get('visitorUUID', 'current_uuid');
        existingUUID = uuidData ? uuidData.uuid : null;
      } catch (error) {
        console.error('[App] Error getting existing UUID:', error);
      }
      
      console.log('[App] Token and UUID check:', {
        hasLocalStorageToken: !!localStorage.getItem('auth_token'),
        hasCookieToken: !!Cookies.get(COOKIE_NAME),
        tokenLength: token ? token.length : 0,
        hasExistingUUID: !!existingUUID
      });

      // If no token OR no UUID, reset session to get both
      if (!token || !existingUUID) {
        console.log('[App] Missing token or UUID, resetting session...', {
          hasToken: !!token,
          hasUUID: !!existingUUID
        });
        clearTimeout(globalTimeout);
        await resetSession();
        return;
      }

      if (token) {
        console.log('[App] Found existing token, attempting to validate...');
        try {
          const decoded = await decodeJwtFromServer(token);
          console.log('[App] Token decoded successfully:', { uuid: decoded.uuid, exp: decoded.exp });
          setUser(decoded);

          // Set existing UUID in storage with timeout
          console.log('[App] Setting UUID in storage...');
          try {
            // Add timeout to prevent hanging
            const uuidPromise = prysmDB.set('visitorUUID', 'current_uuid', { uuid: decoded.uuid });
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('UUID setting timeout')), 5000)
            );

            await Promise.race([uuidPromise, timeoutPromise]);

            // Dispatch event to notify components
            window.dispatchEvent(new Event('uuidUpdated'));
            console.log('[App] UUID set and event dispatched');
          } catch (uuidError) {
            console.error('[App] Error setting UUID in storage:', uuidError);
            // Continue anyway, this shouldn't block the auth flow
            // Still dispatch the event even if storage failed
            window.dispatchEvent(new Event('uuidUpdated'));
          }

          try {
            // Make API call to backend with user info
            console.log('[App] Making API call to getUserData...');
            console.log('[App] Backend URL:', BACKEND_URL);

            // Add timeout to API call
            const apiPromise = apiService.getUserData();
            const apiTimeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('API call timeout')), 10000)
            );

            const response = await Promise.race([apiPromise, apiTimeoutPromise]);
            console.log('[App] API response received:', {
              ok: response.ok,
              status: response.status,
              statusText: response.statusText
            });

            const responseData = await response.json();
            console.log('[App] Response data parsed:', {
              success: responseData.success,
              hasData: !!responseData.data
            });

            if (responseData.success && responseData.data) {
              console.log('[App] Storing registration data in storage...');
              // Add timeout to registration data storage
              const regDataPromise = prysmDB.set('registration_data', 'current_registration', responseData.data);
              const regDataTimeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Registration data storage timeout')), 5000)
              );

              await Promise.race([regDataPromise, regDataTimeoutPromise]);

              // Dispatch event to notify other components of data update
              window.dispatchEvent(new CustomEvent('registrationDataUpdated', { detail: responseData.data }));
              console.log('[App] Registration data stored and event dispatched');

              setError(null);
            } else {
              // If no user data is available, just continue without requesting new session
              console.log('[App] No user data available, continuing with existing session...');
              setError(null);
            }
          } catch (fetchError) {
            console.error('[App] Fetch error details:', {
              message: fetchError.message,
              name: fetchError.name,
              stack: fetchError.stack,
              type: typeof fetchError
            });

            if (fetchError.message.includes('Failed to fetch') ||
              fetchError.message.includes('ERR_CONNECTION_REFUSED') ||
              fetchError.message.includes('NetworkError')) {
              console.log('[App] Network error detected, setting connection error');
              setError({
                message: 'Unable to connect to OurPrsym at this time.',
                type: 'connection',
                details: `Trying to connect to: ${BACKEND_URL}`
              });
              setIsLoading(false);
              return;
            } else {
              console.log('[App] API error detected, continuing with existing session');
              setError(null); // Clear error and continue
            }
          } finally {
            // Clear global timeout and ensure loading state is always cleared
            clearTimeout(globalTimeout);
            console.log('[App] Authentication check completed, setting loading to false');
            setIsLoading(false);
          }
        } catch (tokenError) {
          console.error('[App] Token error details:', {
            message: tokenError.message,
            name: tokenError.name,
            stack: tokenError.stack
          });
          console.log('[App] Token validation failed, resetting session...');
          clearTimeout(globalTimeout);
          await resetSession();
        }
      }
    };





    console.log('[App] Starting checkAuth...');
    checkAuth().catch(error => {
      console.error('[App] Unhandled error in checkAuth:', error);
      setError({
        message: 'An unexpected error occurred during initialization.',
        type: 'unexpected'
      });
      setIsLoading(false);
    });
  }, []);

  // Utility function to get auth token for API calls
  const getAuthToken = () => {
    // Try to get token from localStorage first (from server response)
    const localToken = localStorage.getItem('auth_token');
    if (localToken) {
      return localToken;
    }

    // Fallback to cookie
    const cookieToken = Cookies.get(COOKIE_NAME);
    if (cookieToken) {
      return cookieToken;
    }

    return null;
  };

  // Expose functions globally for other components to use
  window.resetSession = resetSession;
  window.getAuthToken = getAuthToken;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
      <Navbar />
      <Welcome user={user} error={error} isLoading={isLoading} />
    </div>
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <AppContent />,
    errorElement: <ErrorBoundary />
  },
  {
    path: "/register",
    element: (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
        <Navbar />
        <Registration />
      </div>
    ),
    errorElement: <ErrorBoundary />
  },
  {
    path: "/upload-selfie",
    element: (
      <RegistrationGuard>
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
          <Navbar />
          <SelfieUpload />
        </div>
      </RegistrationGuard>
    ),
    errorElement: <ErrorBoundary />
  },
  {
    path: "/upload-license",
    element: (
      <RegistrationGuard>
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
          <Navbar />
          <LicenseUpload />
        </div>
      </RegistrationGuard>
    ),
    errorElement: <ErrorBoundary />
  },
  {
    path: "/profile",
    element: (
      <RegistrationGuard>
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
          <Navbar />
          <Profile />
        </div>
      </RegistrationGuard>
    ),
    errorElement: <ErrorBoundary />
  },
  {
    path: "/checkout",
    element: (
      <RegistrationGuard>
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
          <Navbar />
          <Checkout />
        </div>
      </RegistrationGuard>
    ),
    errorElement: <ErrorBoundary />
  },
  {
    path: "/sms-verification",
    element: (
      <RegistrationGuard>
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
          <Navbar />
          <SMSVerification />
        </div>
      </RegistrationGuard>
    ),
    errorElement: <ErrorBoundary />
  },
  {
    path: "/payment",
    element: (
      <RegistrationGuard>
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
          <Navbar />
          <Payment />
        </div>
      </RegistrationGuard>
    ),
    errorElement: <ErrorBoundary />
  },
  {
    path: "/square-payment",
    element: (
      <RegistrationGuard>
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
          <Navbar />
          <SquarePaymentCard />
        </div>
      </RegistrationGuard>
    ),
    errorElement: <ErrorBoundary />
  },
  {
    path: "/thank-you",
    element: (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
        <Navbar />
        <ThankYou />
      </div>
    ),
    errorElement: <ErrorBoundary />
  },
  {
    path: "/support-qa",
    element: (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
        <Navbar />
        <SupportQA />
      </div>
    ),
    errorElement: <ErrorBoundary />
  },
  {
    path: "*",
    element: <NotFound />
  }
], {
  basename: "/",
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
});

console.log('[App] Router created with basename /');

function App() {
  console.log('[App] Main App component rendering...');
  console.log('[App] Router configuration:', {
    basename: '/',
    routes: router.routes?.map(r => ({ path: r.path, element: r.element?.type?.name || 'Unknown' }))
  });
  return (
    <div className="App">
      <RouterProvider router={router} />
    </div>
  );
}

export default App; 